import { CheckIcon, XIcon, ClockIcon, VehicleIcon, AlertTriangleIcon, RotateCcwIcon } from '../Layout/icons';
import { normalizeDeliveryStateFromApi } from '../../utils/deliveryState';

// Valores do enum do backend
export const DELIVERY_STATUS = {
    CREATION: 'CREATION',
    CONFIRMATION: 'CONFIRMATION',
    PENDING: 'PENDING',
    IN_TRANSIT: 'IN_TRANSIT',
    OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
    DELIVERY_FAILURE: 'DELIVERY_FAILURE',
    RESCHEDULED: 'RESCHEDULED',
    DELIVERED: 'DELIVERED',
    RETURNED: 'RETURNED',
    CANCELLED: 'CANCELLED',
};

/*
 * Paleta de status — evolução do ciclo de vida de uma entrega:
 *
 *  CREATION  →  CONFIRMATION  →  PENDING  →  IN_TRANSIT  →  OUT_FOR_DELIVERY  →  DELIVERED
 *   (gray)        (amber)        (indigo)      (blue)            (teal)            (green)
 *
 * Desvios do caminho feliz:
 *   DELIVERY_FAILURE (orange — falha recuperável)
 *   RESCHEDULED      (amber  — aguardando nova data)
 *   RETURNED         (red    — retorno definitivo)
 *   CANCELLED        (red    — encerramento definitivo)
 *
 * Contraste mínimo WCAG AA (4,5:1) garantido em todos os tokens.
 */
export const DELIVERY_STATUS_STYLE_MAP = {
    [DELIVERY_STATUS.CREATION]: {
        category: 'creation',
        label: 'Em criação',
        colorToken: 'gray',
        icon: null,
    },
    [DELIVERY_STATUS.CONFIRMATION]: {
        category: 'awaiting',
        label: 'Aguardando confirmação',
        colorToken: 'amber',
        icon: ClockIcon,
    },
    [DELIVERY_STATUS.PENDING]: {
        category: 'pending',
        label: 'Pendente',
        colorToken: 'indigo',
        icon: null,
    },
    [DELIVERY_STATUS.IN_TRANSIT]: {
        category: 'inProgress',
        label: 'Em trânsito',
        colorToken: 'blue',
        icon: VehicleIcon,
    },
    [DELIVERY_STATUS.OUT_FOR_DELIVERY]: {
        category: 'inProgress',
        label: 'Saiu para entrega',
        colorToken: 'teal',
        icon: VehicleIcon,
    },
    [DELIVERY_STATUS.DELIVERY_FAILURE]: {
        category: 'warning',
        label: 'Falha na entrega',
        colorToken: 'orange',
        icon: AlertTriangleIcon,
    },
    [DELIVERY_STATUS.RESCHEDULED]: {
        category: 'warning',
        label: 'Reagendado',
        colorToken: 'amber',
        icon: ClockIcon,
    },
    [DELIVERY_STATUS.DELIVERED]: {
        category: 'success',
        label: 'Entregue',
        colorToken: 'green',
        icon: CheckIcon,
    },
    [DELIVERY_STATUS.RETURNED]: {
        category: 'error',
        label: 'Retornou ao remetente',
        colorToken: 'red',
        icon: RotateCcwIcon,
    },
    [DELIVERY_STATUS.CANCELLED]: {
        category: 'error',
        label: 'Cancelado',
        colorToken: 'red',
        icon: XIcon,
    },
};

export function getDeliveryStatusValue(apiStateOrValue) {
    if (!apiStateOrValue) return '';
    if (typeof apiStateOrValue === 'string') return apiStateOrValue;
    if (typeof apiStateOrValue === 'object' && apiStateOrValue.value) {
        return String(apiStateOrValue.value);
    }
    return '';
}

export function getDeliveryStatusMeta(apiStateOrValue) {
    const value = getDeliveryStatusValue(apiStateOrValue);
    const meta = DELIVERY_STATUS_STYLE_MAP[value];
    if (!meta) {
        return {
            value,
            label: value || '—',
            colorToken: 'gray',
            icon: null,
        };
    }
    return { value, ...meta };
}

/** Opções para filtro/seleção de status (valores do enum + rótulos da UI). */
export function getSelectableDeliveryStatusOptions() {
    return Object.values(DELIVERY_STATUS).map((value) => ({
        value,
        label: DELIVERY_STATUS_STYLE_MAP[value]?.label ?? value,
    }));
}

/**
 * Completa a lista de contagens com todos os valores de DeliveryStateEnum (count 0 nos ausentes).
 * A ordem segue a declaração de {@link DELIVERY_STATUS}.
 * @param {Array<{ status?: string, count?: number }>} items
 * @returns {Array<{ status: string, count: number }>}
 */
export function completeDeliveryStatusCounts(items) {
    const countByStatus = new Map();
    (Array.isArray(items) ? items : []).forEach((item) => {
        const status = normalizeDeliveryStateFromApi(getDeliveryStatusValue(item?.status));
        if (!status) return;
        countByStatus.set(status, Number(item.count) || 0);
    });
    return Object.values(DELIVERY_STATUS).map((status) => ({
        status,
        count: countByStatus.has(status) ? countByStatus.get(status) : 0,
    }));
}

/**
 * Nome do enum Java `DeliveryStateEnum` (não o `value` serializado).
 * Usado em relatórios que esperam `states` com o nome da constante.
 */
export const DELIVERY_STATUS_ENUM_NAME = {
    [DELIVERY_STATUS.CREATION]: 'IN_CREATION',
    [DELIVERY_STATUS.CONFIRMATION]: 'AWAITING_CONFIRMATION',
    [DELIVERY_STATUS.PENDING]: 'PENDING',
    [DELIVERY_STATUS.IN_TRANSIT]: 'IN_TRANSIT',
    [DELIVERY_STATUS.OUT_FOR_DELIVERY]: 'OUT_FOR_DELIVERY',
    [DELIVERY_STATUS.DELIVERY_FAILURE]: 'DELIVERY_FAILURE',
    [DELIVERY_STATUS.RESCHEDULED]: 'RESCHEDULED',
    [DELIVERY_STATUS.DELIVERED]: 'DELIVERED',
    [DELIVERY_STATUS.RETURNED]: 'RETURNED',
    [DELIVERY_STATUS.CANCELLED]: 'CANCELLED',
};

/** Opções de situação para relatório: `value` = nome do enum. */
export function getReportDeliveryStateOptions() {
    return Object.values(DELIVERY_STATUS).map((apiValue) => ({
        value: DELIVERY_STATUS_ENUM_NAME[apiValue],
        label: DELIVERY_STATUS_STYLE_MAP[apiValue]?.label ?? apiValue,
    }));
}

/** Status elegíveis para inclusão em roteiros (consulta do painel de planejamento). */
export const ROUTE_PLANNING_DELIVERY_STATES = [
    DELIVERY_STATUS.PENDING,
    DELIVERY_STATUS.IN_TRANSIT,
    DELIVERY_STATUS.DELIVERY_FAILURE,
    DELIVERY_STATUS.RESCHEDULED,
];

